import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { handleFacebookCallback, connectFacebookPage } from '../services/publishingService';
import Button from '../components/ui/Button';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ArrowPathIcon } from '@heroicons/react/24/solid';

export default function FacebookCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');
  const [facebookPages, setFacebookPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [connectingPage, setConnectingPage] = useState(false);

  useEffect(() => {
    const processCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check for OAuth errors
        if (errorParam) {
          setError(errorDescription || 'Facebook authorization was denied');
          setStatus('error');
          setTimeout(() => navigate('/platforms'), 3000);
          return;
        }

        // Validate required parameters
        if (!code || !state) {
          setError('Invalid callback parameters');
          setStatus('error');
          setTimeout(() => navigate('/platforms'), 3000);
          return;
        }

        // Verify user is authenticated
        if (!currentUser) {
          setError('User not authenticated');
          setStatus('error');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        setStatus('processing');

        // Exchange code for access token and fetch pages
        const result = await handleFacebookCallback(currentUser.uid, code, state);

        if (result.success && result.pages && result.pages.length > 0) {
          setFacebookPages(result.pages);
          setStatus('select_page');
        } else if (result.success) {
          setError('No Facebook pages found. Make sure you have admin access to at least one page.');
          setStatus('error');
          setTimeout(() => navigate('/platforms'), 3000);
        } else {
          setError(result.error || 'Failed to complete Facebook connection');
          setStatus('error');
          setTimeout(() => navigate('/platforms'), 3000);
        }
      } catch (error) {
        console.error('Error processing Facebook callback:', error);
        setError(error.message || 'An unexpected error occurred');
        setStatus('error');
        setTimeout(() => navigate('/platforms'), 3000);
      }
    };

    processCallback();
  }, [searchParams, currentUser, navigate]);

  const handleSelectPage = async () => {
    if (!selectedPage) {
      setError('Please select a Facebook page');
      return;
    }

    try {
      setConnectingPage(true);
      setError('');

      await connectFacebookPage(selectedPage);

      setStatus('success');
      // Redirect to platforms page after 1.5 seconds
      setTimeout(() => navigate('/platforms'), 1500);
    } catch (error) {
      setError(error.message || 'Failed to connect page');
      setConnectingPage(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-secondary flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {status === 'select_page' ? (
          <div className="bg-bg-primary rounded-2xl shadow-lg overflow-hidden">
            <div className="p-6 border-b border-border-primary">
              <h2 className="text-2xl font-bold text-text-primary">
                Select Facebook Page
              </h2>
              <p className="text-text-secondary mt-2">
                Choose which Facebook page you want to connect
              </p>
            </div>

            <div className="p-6">
              {error && (
                <div className="mb-4 bg-status-error/10 border border-status-error/30 rounded-lg p-4">
                  <p className="text-sm text-status-error">{error}</p>
                </div>
              )}

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {facebookPages.map((page) => (
                  <button
                    key={page.id}
                    onClick={() => setSelectedPage(page)}
                    className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                      selectedPage?.id === page.id
                        ? 'border-accent-primary bg-accent-primary/10'
                        : 'border-border-primary hover:border-accent-primary/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {page.picture?.data?.url && (
                          <img
                            src={page.picture.data.url}
                            alt={page.name}
                            className="w-12 h-12 rounded-full"
                          />
                        )}
                        <div>
                          <div className="font-medium text-text-primary">{page.name}</div>
                          <div className="text-sm text-text-secondary">ID: {page.id}</div>
                          {page.followers_count !== undefined && (
                            <div className="text-xs text-text-secondary">
                              {page.followers_count.toLocaleString()} followers
                            </div>
                          )}
                        </div>
                      </div>
                      {selectedPage?.id === page.id && (
                        <CheckCircleIcon className="h-6 w-6 text-accent-primary" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6 border-t border-border-primary bg-bg-secondary">
              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/platforms')}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSelectPage}
                  disabled={!selectedPage || connectingPage}
                  className="bg-accent-primary text-white border-0"
                >
                  {connectingPage ? 'Connecting...' : 'Connect Page'}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-bg-primary rounded-2xl shadow-lg p-8 text-center">
            {status === 'processing' && (
              <>
                <div className="mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                    <ArrowPathIcon className="animate-spin h-8 w-8 text-blue-600" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-text-primary mb-2">
                  Connecting Facebook
                </h2>
                <p className="text-text-secondary">
                  Please wait while we complete the connection...
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <CheckCircleIcon className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-text-primary mb-2">
                  Successfully Connected!
                </h2>
                <p className="text-text-secondary">
                  Your Facebook page has been connected. Redirecting...
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                    <XMarkIcon className="h-8 w-8 text-red-600" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-text-primary mb-2">
                  Connection Failed
                </h2>
                <p className="text-text-secondary mb-4">
                  {error}
                </p>
                <p className="text-sm text-text-muted">
                  Redirecting to platforms page...
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
